// TeamSelectScreen — chalkboard fullscreen shown when the assembled match
// has at least one NPC slot. Lets humans swap into NPC slots before the
// map vote begins. 10 s countdown driven by the server's
// teamSelectCountdown field.
//
// Background reuses /ui/map-selection-bg.webp so it visually merges with
// MapVoteScreen — the user explicitly asked for these two to share BG +
// fonts.

import { menuAudio } from '../MenuAudio';
import { MenuNav, type MenuItem } from '../MenuNav';
import { StageScaler, STAGE_W, STAGE_H } from '../StageScaler';
import { renderHeadIcon } from '../HeadIcon';
import type { HatId } from '../../render/HatModels';
import { t } from '../../i18n';

const WIPE_DURATION_MS = 700;

export interface TeamSelectMember {
  charId: number;
  name: string;
  color: string;
  hatId: HatId | null;
  team: 'red' | 'blue';
  isLocal: boolean;
  isNpc: boolean;
  isReady: boolean;
  /** Account-derived player level. Defaults to 1 for unauthed / NPC slots. */
  level?: number;
}

export interface TeamSelectCallbacks {
  onSwapWithNpc(npcCharId: number): void;
  onReady(): void;
  /** Fired when user presses Escape — caller routes to ConfirmationPopup
   *  for "Leave online play?". */
  onRequestLeave(): void;
}

const STYLES_ID = 'team-select-styles';
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Chewy&display=swap');

#team-select {
  position: fixed; inset: 0; z-index: 99;
  background: url('/ui/map-selection-bg.webp') center center / cover no-repeat #1a1a1a;
  font-family: 'Chewy', ui-sans-serif, system-ui, sans-serif;
  color: #FFFFFF;
  overflow: hidden;
  clip-path: inset(0 0 0 0);
  transition: clip-path ${WIPE_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1);
}
#team-select.wiping-out { clip-path: inset(0 100% 0 0); pointer-events: none; }

#team-select .stage {
  position: absolute; top: 50%; left: 50%;
  width: ${STAGE_W}px; height: ${STAGE_H}px;
  transform-origin: center center;
  transform: translate(-50%, -50%) scale(1);
}

#team-select .title {
  position: absolute; left: 0; right: 0; top: 80px;
  text-align: center;
  font-size: 48px; line-height: 1;
}
#team-select .title .countdown {
  color: #FFB84D;
  text-shadow: 0 -3px 0 rgba(155, 60, 0, 0.4);
  display: inline-block;
  min-width: 1ch;
  margin-left: 14px;
  font-size: 56px;
}

#team-select .teams {
  position: absolute;
  left: 50%; top: 380px;
  transform: translateX(-50%);
  display: flex; gap: 40px;
}

#team-select .team-panel {
  background: #1E232C;
  border-radius: 18px;
  box-shadow: 0 16px 48px rgba(0,0,0,0.5);
  padding: 12px;
  width: 380px;
  display: flex; flex-direction: column;
  gap: 10px;
}
#team-select .team-panel .team-header {
  border-radius: 10px;
  padding: 6px 12px;
  font-size: 22px;
  letter-spacing: 0.06em;
  text-align: center;
  -webkit-text-stroke: 3px rgba(0,0,0,0.22);
  paint-order: stroke fill;
}
#team-select .team-panel.red .team-header { background: #FF4D4D; }
#team-select .team-panel.blue .team-header { background: #3DA6FF; }

#team-select .row {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 12px;
  background: #FFFFFF;
  color: #1E232C;
  border-radius: 12px;
  position: relative;
}
#team-select .row .row-head {
  width: 32px; height: 32px;
  border-radius: 50%;
  background-position: center 22%;
  background-size: cover;
  border: 2px solid rgba(0,0,0,0.18);
  flex-shrink: 0;
}
#team-select .row .row-name {
  flex: 1;
  font-size: 22px;
  line-height: 1;
}
#team-select .row .chip {
  font-size: 14px;
  padding: 4px 10px;
  border-radius: 8px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
#team-select .row .chip.you { background: #FFB84D; color: #FFFFFF; }
#team-select .row .chip.npc { background: #B8C2CB; color: #FFFFFF; }
#team-select .row .row-lvl {
  font-size: 14px;
  padding: 4px 10px;
  border-radius: 8px;
  background: linear-gradient(180deg, #6FCEEB 0%, #3DA6FF 100%);
  color: #FFFFFF;
  -webkit-text-stroke: 1px #2A6DA0;
  paint-order: stroke fill;
  letter-spacing: 0.04em;
  flex-shrink: 0;
}
#team-select .row .ready-mark {
  width: 22px; height: 22px;
  border-radius: 50%;
  background: #2BC275;
  display: inline-flex; align-items: center; justify-content: center;
  color: #FFFFFF;
  font-size: 14px;
  font-family: 'Chewy', sans-serif;
}
#team-select .row.you-row.focused {
  outline: 4px solid #FFB84D;
  outline-offset: 3px;
}
#team-select .row.npc-row { cursor: pointer; }
#team-select .row.npc-row:hover { outline: 3px solid rgba(255, 184, 77, 0.6); outline-offset: 2px; }

#team-select .ready-btn {
  position: absolute;
  left: 50%; bottom: 120px;
  transform: translateX(-50%);
  background: linear-gradient(180deg, #FFAA66 0%, #FF8A1E 100%);
  border: 0;
  border-radius: 24px;
  cursor: pointer;
  padding: 16px 56px;
  font-family: 'Chewy', sans-serif;
  font-size: 36px;
  color: #FFFFFF;
  -webkit-text-stroke: 3px #B14400;
  paint-order: stroke fill;
  box-shadow: 0 6px 0 #B14400, 0 12px 22px rgba(0,0,0,0.3);
  letter-spacing: 0.04em;
  transition: transform 140ms ease, box-shadow 140ms ease, filter 140ms ease;
}
#team-select .ready-btn:hover,
#team-select .ready-btn.focused { filter: brightness(1.04); outline: 4px solid rgba(255,255,255,0.6); outline-offset: 4px; }
#team-select .ready-btn:active { transform: translateX(-50%) translateY(3px); box-shadow: 0 3px 0 #B14400; }
`;

export class TeamSelectScreen {
  private root: HTMLElement | null = null;
  private styleEl: HTMLStyleElement | null = null;
  private scaler: StageScaler | null = null;
  private nav: MenuNav | null = null;
  private members: TeamSelectMember[] = [];
  private localCharId: number | null = null;
  private countdownSec = 10;
  private resolved = false;
  private cleanupKeydown: (() => void) | null = null;
  private callbacks: TeamSelectCallbacks | null = null;
  /** Hash of the last-rendered roster. Used to short-circuit
   *  renderTeams() when the controller fires updateFromState every state
   *  patch (every second the countdown ticks). innerHTML rebuilds + async
   *  head-icon rerenders during a flicker were directly visible. */
  private lastMembersFingerprint = '';

  /** Mount the screen. Returns immediately — call setMembers / setCountdown
   *  to drive UI state. Call leaveScreen() to wipe out + dispose. */
  mount(callbacks: TeamSelectCallbacks): void {
    if (this.root) return;
    this.styleEl = document.createElement('style');
    this.styleEl.textContent = STYLES;
    if (!document.getElementById(STYLES_ID)) {
      this.styleEl.id = STYLES_ID;
      document.head.appendChild(this.styleEl);
    } else {
      this.styleEl = null;
    }
    const root = document.createElement('div');
    root.id = 'team-select';
    root.innerHTML = `
      <div class="stage">
        <div class="title">${t('team.chooseTeam')}<span class="countdown">10</span></div>
        <div class="teams">
          <div class="team-panel red" data-team="red"></div>
          <div class="team-panel blue" data-team="blue"></div>
        </div>
        <button type="button" class="ready-btn">${t('team.ready')}</button>
      </div>
    `;
    document.body.appendChild(root);
    this.root = root;
    const stageEl = root.querySelector('.stage') as HTMLElement;
    this.scaler = new StageScaler({ stageEl });
    this.scaler.start();
    menuAudio.playMapScreenSweep();
    this.installControls(callbacks);
  }

  setMembers(members: ReadonlyArray<TeamSelectMember>, localCharId: number): void {
    // Fingerprint everything that affects the rendered roster — charId,
    // name, color, team, isLocal, isNpc, isReady. The countdown ticks
    // every second and refreshTeamSelectFromState fires alongside, but
    // those state patches don't change the roster layout. Skip the
    // innerHTML rebuild + async head-icon rerender when nothing changed.
    const fingerprint = members
      .map((m) => `${m.charId}|${m.name}|${m.color}|${m.team}|${m.isLocal ? '1' : '0'}|${m.isNpc ? '1' : '0'}|${m.isReady ? '1' : '0'}|${m.level ?? 0}`)
      .join('§') + `#${localCharId}`;
    if (fingerprint === this.lastMembersFingerprint) {
      this.members = members.slice();
      this.localCharId = localCharId;
      return;
    }
    this.lastMembersFingerprint = fingerprint;
    this.members = members.slice();
    this.localCharId = localCharId;
    void this.localCharId; // reserved for future ready-mark / focus logic
    if (this.root) this.renderTeams();
  }

  setCountdown(secondsRemaining: number): void {
    this.countdownSec = Math.max(0, secondsRemaining);
    if (!this.root) return;
    const el = this.root.querySelector('.countdown') as HTMLElement | null;
    if (el) el.textContent = String(this.countdownSec);
  }

  async leaveScreen(): Promise<void> {
    if (!this.root || this.resolved) return;
    this.resolved = true;
    menuAudio.playMapScreenSweep();
    this.nav?.dispose();
    this.nav = null;
    this.root.classList.add('wiping-out');
    await new Promise((r) => setTimeout(r, WIPE_DURATION_MS));
    this.dispose();
  }

  dispose(): void {
    if (!this.root) return;
    this.scaler?.stop();
    this.scaler = null;
    this.cleanupKeydown?.();
    this.cleanupKeydown = null;
    this.nav?.dispose();
    this.nav = null;
    this.root.remove();
    this.styleEl?.remove();
    this.root = null;
    this.styleEl = null;
    this.callbacks = null;
  }

  // -- internals ----------------------------------------------------------

  private installControls(callbacks: TeamSelectCallbacks): void {
    if (!this.root) return;
    const readyBtn = this.root.querySelector('.ready-btn') as HTMLButtonElement;

    const onKeyDown = (e: KeyboardEvent): void => {
      if (!this.root) return;
      if (e.code === 'Escape' || e.code === 'Backspace') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        callbacks.onRequestLeave();
        return;
      }
      // Left/Right while focused on the YOU row attempts a team swap with
      // an NPC on the opposite team. We must intercept BEFORE MenuNav's
      // own keydown handler runs — they're both on `window` with capture
      // (same target same phase), so registration order wins. This
      // listener registers in installControls(), which is called from
      // mount() BEFORE renderTeams → rebuildNav → MenuNav.mount(), so
      // ours fires first. stopImmediatePropagation then prevents
      // MenuNav from seeing the event at all.
      if (e.code === 'ArrowLeft' || e.code === 'ArrowRight') {
        const focusedYou = this.root.querySelector('.row.you-row.focused');
        if (focusedYou) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.attemptSwapNpc(callbacks, e.code === 'ArrowRight' ? 'right' : 'left');
        }
      }
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    this.cleanupKeydown = () => {
      window.removeEventListener('keydown', onKeyDown, { capture: true } as EventListenerOptions);
    };

    // Build MenuNav with two rows: YOU row + Ready button.
    // The YOU row element is dynamic (re-rendered on team swap) so we
    // re-build the nav each render via rebuildNav().
    this.callbacks = callbacks;
    void readyBtn;
  }

  private renderTeams(): void {
    if (!this.root) return;
    const redPanel = this.root.querySelector('.team-panel.red') as HTMLElement;
    const bluePanel = this.root.querySelector('.team-panel.blue') as HTMLElement;
    redPanel.innerHTML = `<div class="team-header">${t('team.redTeam')}</div>` + this.renderRows('red');
    bluePanel.innerHTML = `<div class="team-header">${t('team.blueTeam')}</div>` + this.renderRows('blue');
    // Fill avatar canvases async.
    for (const m of this.members) {
      void this.fillRowHead(m);
    }
    // Mouse: clicking an NPC row swaps the local player into that specific slot.
    const npcRows = this.root.querySelectorAll('.row.npc-row');
    npcRows.forEach((rowEl) => {
      rowEl.addEventListener('click', () => {
        if (!this.callbacks) return;
        const charIdAttr = (rowEl as HTMLElement).dataset.charId;
        if (charIdAttr === undefined) return;
        const npcCharId = Number(charIdAttr);
        if (!Number.isFinite(npcCharId)) return;
        menuAudio.playClick();
        this.callbacks.onSwapWithNpc(npcCharId);
      });
    });
    this.rebuildNav();
  }

  private renderRows(team: 'red' | 'blue'): string {
    const rows = this.members
      .filter((m) => m.team === team)
      .sort((a, b) => a.charId - b.charId);
    return rows.map((m) => {
      const isYou = m.isLocal;
      const cls = `row ${isYou ? 'you-row' : ''} ${m.isNpc ? 'npc-row' : ''}`.trim();
      // Chip priority: ready check wins over both YOU and NPC labels so a
      // ready local player can see their own ✓ instead of being stuck on
      // the YOU pill. NPC tag and YOU tag only render for unready slots
      // (NPCs never get ready=true server-side).
      let chip = '';
      if (m.isReady) {
        chip = `<span class="ready-mark">✓</span>`;
      } else if (m.isNpc) {
        chip = `<span class="chip npc">${t('team.npc')}</span>`;
      } else if (isYou) {
        chip = `<span class="chip you">${t('team.you')}</span>`;
      }
      const lvlBadge = !m.isNpc && m.level && m.level > 0
        ? `<span class="row-lvl">LVL ${m.level}</span>`
        : '';
      return `
        <div class="${cls}" data-char-id="${m.charId}">
          <span class="row-head" id="ts-row-head-${m.charId}" style="background:${escapeAttr(m.color)};"></span>
          <span class="row-name">${escapeHtml(m.name)}</span>
          ${lvlBadge}
          ${chip}
        </div>
      `;
    }).join('');
  }

  private async fillRowHead(m: TeamSelectMember): Promise<void> {
    if (!this.root) return;
    try {
      const canvas = await renderHeadIcon({ color: m.color, hatId: m.hatId, size: 64 });
      const url = canvas.toDataURL('image/png');
      const el = this.root.querySelector(`#ts-row-head-${m.charId}`) as HTMLElement | null;
      if (!el) return;
      el.style.backgroundImage = `url(${url})`;
      el.style.backgroundColor = 'transparent';
    } catch { /* keep flat color fallback */ }
  }

  private rebuildNav(): void {
    if (!this.root || !this.callbacks) return;
    this.nav?.dispose();
    const items: MenuItem[] = [];
    const youRow = this.root.querySelector('.row.you-row') as HTMLElement | null;
    if (youRow) {
      items.push({ el: youRow, row: 0, col: 0, onActivate: () => { /* no-op: arrow keys drive swaps */ } });
    }
    const readyBtn = this.root.querySelector('.ready-btn') as HTMLElement;
    items.push({ el: readyBtn, row: 1, col: 0, onActivate: () => this.callbacks!.onReady() });
    this.nav = new MenuNav({ items, initialIndex: 0 });
    this.nav.mount();
  }

  private attemptSwapNpc(callbacks: TeamSelectCallbacks, dir: 'left' | 'right'): void {
    void dir; // direction is informational only — we always swap with the
              // "first available" NPC on the opposite team
    const local = this.members.find((m) => m.isLocal);
    if (!local) return;
    const oppositeTeam = local.team === 'red' ? 'blue' : 'red';
    const npc = this.members
      .filter((m) => m.team === oppositeTeam && m.isNpc)
      .sort((a, b) => a.charId - b.charId)[0];
    if (!npc) {
      menuAudio.playClick();
      return;
    }
    menuAudio.playClick();
    callbacks.onSwapWithNpc(npc.charId);
  }

}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

function escapeAttr(s: string): string {
  return s.replace(/["<>&]/g, (c) => ({ '"': '&quot;', '<': '&lt;', '>': '&gt;', '&': '&amp;' })[c]!);
}
